import React, { useEffect, useId, useRef, useState } from "react";
import { Box, Chip, CircularProgress, InputBase, Typography } from "@mui/material";
import { RiAttachmentLine, RiNotification3Line, RiSendPlane2Line } from "@remixicon/react";
import { getAttachmentUrl, MAX_ATTACHMENT_BYTES } from "../../services/dataService";
import { composerLocked, MESSAGE_MAX } from "../../utils/ticketContract";
import { countText } from "../../utils/kitPresentation";
import { conversationText, messageCount, messageMeta, openedChip } from "../../utils/ticketDetail";
import { tokens } from "../../utils/figmaTokens";
import SmallButton from "../../components/kit/SmallButton";
import { ErrorState } from "../../components/kit/States";
import { Line, Panel, PanelHead } from "./TicketParts";

// Download URLs for the thread's images, each path asked for once. A file that cannot be read is
// null, so one bad image does not blank the thread.
const useAttachmentUrls = (messages) => {
  const asked = useRef(new Set());
  const [urls, setUrls] = useState({});
  useEffect(() => {
    const paths = [...new Set(messages.map((m) => m.attachmentPath).filter(Boolean))].filter((p) => !asked.current.has(p));
    paths.forEach((path) => {
      asked.current.add(path);
      getAttachmentUrl(path)
        .then((url) => setUrls((prev) => ({ ...prev, [path]: url })))
        .catch(() => setUrls((prev) => ({ ...prev, [path]: null })));
    });
  }, [messages]);
  return urls;
};

// One message (Message · Support, Message · Family): staff on the right in brand blue, the person
// on the left in grey, the line under the text naming who and when. An image opens full size in
// a new tab.
const Message = ({ message, meta, url }) => (
  <Box
    sx={{
      alignSelf: meta.staff ? "flex-end" : "flex-start",
      maxWidth: 548,
      px: "14px",
      py: "10px",
      borderRadius: "12px",
      bgcolor: meta.staff ? tokens.brand.subtle : tokens.surface.muted,
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    }}
  >
    <Typography variant="bodySmall" sx={{ color: tokens.text.primary, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
      {message.text}
    </Typography>
    {message.attachmentPath &&
      (url === undefined ? (
        <CircularProgress size={14} />
      ) : url === null ? (
        <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
          Attachment unavailable.
        </Typography>
      ) : (
        <Box component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ display: "block" }}>
          <Box component="img" src={url} alt="Attachment" sx={{ display: "block", maxWidth: 240, maxHeight: 240, borderRadius: "8px" }} />
        </Box>
      ))}
    <Typography variant="labelMedium" component="div" sx={{ color: tokens.text.secondary, textAlign: meta.staff ? "right" : "left" }}>
      {meta.line}
    </Typography>
  </Box>
);

// The conversation (D60; Conversation on 10829:93565): the thread after the opening message, and
// the composer for REPLY_INBOX. `onSend(text, file)` resolves true once the reply is saved, and the
// box then clears; on false it keeps what was typed. Request info (while Submitted) and Send
// notification show on a ticket a person raised; the page passes their handlers only then.
const ConversationCard = ({
  ticket,
  users,
  thread,
  loading,
  error,
  onRetry,
  canReply,
  onSend,
  onRequestInfo,
  moving = false,
  onNotify,
  notifyReady = false,
}) => {
  const text = conversationText(ticket, users);
  const urls = useAttachmentUrls(thread);
  const fileInput = useRef(null);
  const countId = useId();
  const [draft, setDraft] = useState("");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [sending, setSending] = useState(false);
  const length = draft.trim().length;
  const tooLong = length > MESSAGE_MAX;
  const canSend = length > 0 && !tooLong && !sending;

  const pickFile = (e) => {
    const picked = e.target.files && e.target.files[0];
    e.target.value = ""; // lets the same file be picked again
    if (!picked) return;
    if (!picked.type.startsWith("image/")) {
      setFileError("Only image attachments are allowed.");
      return;
    }
    if (picked.size > MAX_ATTACHMENT_BYTES) {
      setFileError(`That image is over ${MAX_ATTACHMENT_BYTES / (1024 * 1024)}MB.`);
      return;
    }
    setFileError("");
    setFile(picked);
  };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    const sent = await onSend(draft.trim(), file);
    setSending(false);
    if (sent) {
      setDraft("");
      setFile(null);
      setFileError("");
    }
  };

  return (
    <Panel gap="16px">
      <PanelHead title={text.title} line={text.line} aside={<Chip label={messageCount(thread.length)} />} />
      <Line />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <Chip label={openedChip(ticket)} sx={{ alignSelf: "center", maxWidth: "100%" }} />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : error ? (
          <ErrorState compact title="Could not load the messages" message={error} onRetry={onRetry} />
        ) : thread.length === 0 ? (
          <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary, textAlign: "center" }}>
            {text.empty}
          </Typography>
        ) : (
          thread.map((m) => (
            <Message key={m.id} message={m} meta={messageMeta(ticket, m, users)} url={m.attachmentPath ? urls[m.attachmentPath] : undefined} />
          ))
        )}
      </Box>

      {composerLocked(ticket.status) ? (
        <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary, textAlign: "center" }}>
          This support request is closed.
        </Typography>
      ) : (
        canReply && (
          <Box
            sx={{ border: `1px solid ${tokens.border.default}`, borderRadius: "12px", p: "12px", display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <InputBase
              multiline
              minRows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={text.placeholder}
              disabled={sending}
              inputProps={{ "aria-label": "Reply", "aria-describedby": countId }}
              sx={{
                px: "12px",
                py: "10px",
                minHeight: 72,
                alignItems: "flex-start",
                typography: "bodySmall",
                color: tokens.text.primary,
                "& textarea::placeholder": { color: tokens.text.secondary, opacity: 1 },
              }}
            />
            {(file || fileError) && (
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                {file && <Chip label={file.name} onDelete={sending ? undefined : () => setFile(null)} sx={{ maxWidth: 240 }} />}
                {fileError && (
                  <Typography variant="labelMedium" role="alert" sx={{ color: tokens.error.ink }}>
                    {fileError}
                  </Typography>
                )}
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
              <input ref={fileInput} type="file" accept="image/*" hidden onChange={pickFile} />
              <SmallButton Icon={RiAttachmentLine} label="Attach an image" onClick={() => fileInput.current?.click()} disabled={sending} />
              {onRequestInfo && (
                <SmallButton onClick={onRequestInfo} disabled={moving}>
                  Request info
                </SmallButton>
              )}
              {onNotify && (
                <SmallButton Icon={RiNotification3Line} onClick={onNotify} disabled={!notifyReady}>
                  Send notification
                </SmallButton>
              )}
              <Box sx={{ flex: 1 }} />
              <Typography id={countId} variant="labelMedium" sx={{ color: tooLong ? tokens.error.ink : tokens.text.tertiary }}>
                {countText(length)} / {countText(MESSAGE_MAX)}
              </Typography>
              <SmallButton tone="solid" Icon={RiSendPlane2Line} onClick={send} disabled={!canSend}>
                {sending ? "Sending…" : "Send reply"}
              </SmallButton>
            </Box>
          </Box>
        )
      )}
    </Panel>
  );
};

export default ConversationCard;
